import { useQuery } from "@apollo/client";
import { GET_PROJECT } from "api/query";
import type {
  GetProjectQuery,
  GetProjectQueryVariables,
} from "graphql/generated";
import { TrialStatus, TrialType } from "graphql/generated";
import dayjs from "dayjs";
import { isNil } from "es-toolkit";
import { isEmpty } from "es-toolkit/compat";
import { useProjectId } from "./useProjectId";

type ProjectBillingAccount = GetProjectQuery["getProject"]["billingAccount"];

const CREDIT_LIMIT_WARNING_USD = 100;

const getTrialDaysLeft = (billingAccount: ProjectBillingAccount) => {
  if (!billingAccount) return 0;

  const trialEndTimestamp =
    billingAccount.trialType === TrialType.Usage
      ? billingAccount.trialUsage?.endDate
      : billingAccount.trialType === TrialType.Duration
        ? billingAccount.trialEndDate
        : null;

  if (!trialEndTimestamp) return 0;

  const days = dayjs(trialEndTimestamp).diff(dayjs(), "days");

  return days > 0 ? days : 0;
};

const getCredits = (billingAccount: ProjectBillingAccount) => {
  const creditsRemaining = billingAccount?.trialUsage?.remainingUSD;

  if (creditsRemaining == null) return null;

  return {
    creditsRemaining,
    creditsRemainingFormattedShort: creditsRemaining.toLocaleString("en-US", {
      maximumFractionDigits: 0,
    }),
    creditsRemainingFormattedLong: creditsRemaining.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    creditLimitWarning:
      creditsRemaining < CREDIT_LIMIT_WARNING_USD && creditsRemaining > 0,
  };
};

const getHasAnyPaymentMethod = (billingAccount: ProjectBillingAccount) =>
  Boolean(
    billingAccount?.isUsingInvoicing ||
    !isEmpty(billingAccount?.paymentMethods),
  );

export const useProject = () => {
  const projectId = useProjectId();

  const { data, loading, error, refetch } = useQuery<
    GetProjectQuery,
    GetProjectQueryVariables
  >(GET_PROJECT, {
    variables: { id: projectId! },
    skip: !projectId,
  });

  const project = data?.getProject;
  const billingAccount = project?.billingAccount;
  const planType = project?.entitlementSpec?.plan_type;
  const isEmailSupportEnabled =
    project?.entitlementSpec?.support?.email_support ?? false;
  const isSolutionArchitectSupportEnabled =
    project?.entitlementSpec?.support?.solution_architect_support ?? false;

  const trialDaysLeft = getTrialDaysLeft(billingAccount);
  const trialType = billingAccount?.trialType ?? null;
  const trialEndDate = billingAccount?.trialEndDate ?? null;
  const trialStatus = billingAccount?.trialStatus ?? TrialStatus.Unknown;
  const isFreeTrial = billingAccount?.trialStatus
    ? billingAccount.trialStatus === TrialStatus.Current
    : false;
  const trialDaysLeftDateFormatted = trialEndDate
    ? dayjs(trialEndDate).format("MMM D, YYYY")
    : "";
  const isPendingDeletion = Boolean(project?.queuedForDeletion);
  const credits = getCredits(billingAccount);
  const hasAnyPaymentMethod = getHasAnyPaymentMethod(billingAccount);
  // While GET_PROJECT is still loading there's no billingAccount, so the trial
  // math below would read as "out of trial" and lock entitled customers out
  // until the query resolves. The verdict is indeterminate until we have data,
  // so treat it as in-trial while loading (Apollo keeps `loading` false during
  // refetch, so this only covers the initial cold fetch).
  const isOutOfTrial = loading
    ? false
    : hasAnyPaymentMethod
      ? false
      : isNil(trialDaysLeft) ||
        trialDaysLeft <= 0 ||
        (credits !== null && credits.creditsRemaining <= 0);

  return {
    project,
    billingAccount,
    error,
    loading,
    refetch,
    projectName: project?.name ?? "",
    maxVpcs: project?.maxVPCs ?? 3,
    planType,
    isEmailSupportEnabled,
    isSolutionArchitectSupportEnabled,
    trialDaysLeft,
    trialType,
    trialEndDate,
    trialStatus,
    trialDaysLeftDateFormatted,
    isFreeTrial,
    credits,
    hasAnyPaymentMethod,
    isOutOfTrial,
    isPendingDeletion,
  };
};
